import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { isServerBackendEnabled } from '../lib/env';
import { deleteStateKeysRequest, fetchStateRowsRequest, upsertStateRowsRequest } from '../lib/serverApi';
import { sanitizeLegacyDemoBusinessDataInStorage } from '../lib/businessData';
import { sanitizeLegacyCrmDataInStorage } from '../lib/crmData';
import { sanitizeLegacyHeroBannersInStorage } from '../lib/heroBannersData';
import { sanitizeLegacyInsightsInStorage, sanitizeStoredInsights } from '../lib/insightsData';
import { sanitizeLegacyJobPositionsInStorage } from '../lib/jobPositionsData';
import { sanitizeLegacyTestimonialsInStorage } from '../lib/testimonialsData';
import {
  ADMIN_ONLY_KEYS,
  PUBLIC_STATE_KEYS,
  STAFF_SHARED_READ_KEYS,
  STATIC_DEFAULT_STATE,
  STORAGE_KEYS,
  buildUserScopedDefaults,
  buildUserScopedKeys,
  getStorageOwnerFromKey,
  isStaffSharedWriteKey,
  isUserScopedKey,
  isWritableByAdmin,
  shouldSyncStorageKey,
} from '../lib/state/appState';

function LoadingShell() {
  return (
    <div className="min-h-screen bg-[#f9f7f3] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-slate-200 border-t-teal-500 animate-spin" />
        <p className="text-sm text-slate-500">Loading Zobu Consulting…</p>
      </div>
    </div>
  );
}

export default function AppStateSyncProvider({ children }: PropsWithChildren) {
  const { authReady, isAuthenticated, isAdminUser, currentUser } = useAuth();
  const [bootstrapped, setBootstrapped] = useState(!isServerBackendEnabled);
  const hydratingRef = useRef(false);
  const syncQueueRef = useRef(new Map<string, string | null>());
  const flushTimerRef = useRef<number | null>(null);
  const demoCleanupDoneRef = useRef(false);
  const publicSanitizeDoneRef = useRef(false);

  const authScopedKeys = useMemo(() => {
    const keys: string[] = [...STAFF_SHARED_READ_KEYS];
    if (currentUser?.id) {
      keys.push(...buildUserScopedKeys(currentUser.id));
    }
    if (isAdminUser) {
      keys.push(...ADMIN_ONLY_KEYS);
    }
    return keys;
  }, [currentUser?.id, isAdminUser]);

  const clearPrivateState = useCallback(() => {
    hydratingRef.current = true;
    try {
      const keysToRemove = [...STAFF_SHARED_READ_KEYS, ...ADMIN_ONLY_KEYS];
      keysToRemove.forEach((key) => window.localStorage.removeItem(key));
      Object.keys(window.localStorage)
        .filter(
          (key) =>
            key.startsWith(STORAGE_KEYS.portalProjectsPrefix) ||
            key.startsWith(STORAGE_KEYS.portalClientsPrefix) ||
            key.startsWith(STORAGE_KEYS.portalInsightsPrefix),
        )
        .forEach((key) => window.localStorage.removeItem(key));
    } finally {
      hydratingRef.current = false;
    }
  }, []);

  const applyRows = useCallback((rows: { storageKey: string; payloadText: string }[]) => {
    hydratingRef.current = true;
    try {
      rows.forEach((row) => {
        let nextPayload = row.payloadText;

        if (row.storageKey === STORAGE_KEYS.insights) {
          try {
            nextPayload = JSON.stringify(
              sanitizeStoredInsights(JSON.parse(row.payloadText) as Array<unknown> as never[]),
            );
          } catch {
            nextPayload = row.payloadText;
          }
        }

        try {
          window.localStorage.setItem(row.storageKey, nextPayload);
        } catch (error) {
          console.warn(`Skipping storage hydration for ${row.storageKey}`, error);
        }
      });
    } finally {
      hydratingRef.current = false;
    }
  }, []);

  const fetchRows = useCallback(async (keys: string[]) => {
    if (!keys.length) return [];
    const response = await fetchStateRowsRequest(keys);
    return response.rows;
  }, []);

  const seedDefaultsIfMissing = useCallback(async (entries: Record<string, string>) => {
    const keys = Object.keys(entries);
    if (!keys.length) return;

    const existingRows = await fetchRows(keys);
    const existing = new Set(existingRows.map((row) => row.storageKey));
    const missing = keys
      .filter((key) => !existing.has(key))
      .map((key) => ({
        storageKey: key,
        payloadText: window.localStorage.getItem(key) ?? entries[key],
      }));

    if (missing.length === 0) return;
    await upsertStateRowsRequest(missing);
  }, [fetchRows]);

  useEffect(() => {
    if (!isServerBackendEnabled) return;

    let cancelled = false;

    const bootstrap = async () => {
      if (!authReady) return;
      setBootstrapped(false);

      try {
        const publicRows = await fetchRows([...PUBLIC_STATE_KEYS]);
        const publicRowsByKey = new Map(publicRows.map((row) => [row.storageKey, row.payloadText]));
        if (!cancelled) applyRows(publicRows);
        const heroSanitized = sanitizeLegacyHeroBannersInStorage();
        const insightsSanitized = sanitizeLegacyInsightsInStorage();
        const jobPositionsSanitized = sanitizeLegacyJobPositionsInStorage();

        if (isAuthenticated && currentUser) {
          if (isAdminUser) {
            const sanitizedPublicUpserts: Array<{ storageKey: string; payloadText: string }> = [];

            if (heroSanitized) {
              sanitizedPublicUpserts.push({
                storageKey: STORAGE_KEYS.heroBanners,
                payloadText:
                  window.localStorage.getItem(STORAGE_KEYS.heroBanners) ??
                  STATIC_DEFAULT_STATE[STORAGE_KEYS.heroBanners],
              });
            }

            if (insightsSanitized) {
              sanitizedPublicUpserts.push({
                storageKey: STORAGE_KEYS.insights,
                payloadText:
                  window.localStorage.getItem(STORAGE_KEYS.insights) ??
                  STATIC_DEFAULT_STATE[STORAGE_KEYS.insights],
              });
            }

            if (jobPositionsSanitized) {
              sanitizedPublicUpserts.push({
                storageKey: STORAGE_KEYS.jobPositions,
                payloadText:
                  window.localStorage.getItem(STORAGE_KEYS.jobPositions) ??
                  STATIC_DEFAULT_STATE[STORAGE_KEYS.jobPositions],
              });
            }

            if (sanitizedPublicUpserts.length) {
              await upsertStateRowsRequest(sanitizedPublicUpserts);
            }

            // Do not auto-seed shared/content state with default or empty values.
            // This previously allowed a browser with no local content to write
            // empty arrays/default mocks to the server for missing keys.
            // Content-bearing keys should only be created by explicit user edits.

            const recoverablePublicRows = [...PUBLIC_STATE_KEYS].reduce<
              Array<{ storageKey: string; payloadText: string }>
            >((rows, storageKey) => {
              const payloadText = window.localStorage.getItem(storageKey);
              if (
                shouldRecoverServerRow(storageKey, payloadText, publicRowsByKey.get(storageKey) ?? null) &&
                payloadLooksRecoverable(storageKey, payloadText)
              ) {
                rows.push({ storageKey, payloadText });
              }
              return rows;
            }, []);

            if (recoverablePublicRows.length) {
              await upsertStateRowsRequest(recoverablePublicRows);
            }
          }
          await seedDefaultsIfMissing(buildUserScopedDefaults(currentUser.id));
          const authRows = await fetchRows(authScopedKeys);
          const authRowsByKey = new Map(authRows.map((row) => [row.storageKey, row.payloadText]));

          if (isAdminUser) {
            const recoverableAuthRows = authScopedKeys.reduce<Array<{ storageKey: string; payloadText: string }>>(
              (rows, storageKey) => {
                const payloadText = window.localStorage.getItem(storageKey);
                if (
                  shouldRecoverServerRow(storageKey, payloadText, authRowsByKey.get(storageKey) ?? null) &&
                  payloadLooksRecoverable(storageKey, payloadText)
                ) {
                  rows.push({ storageKey, payloadText });
                }
                return rows;
              },
              [],
            );

            if (recoverableAuthRows.length) {
              await upsertStateRowsRequest(recoverableAuthRows);
            }
          }

          if (!cancelled) applyRows(authRows);
        } else if (!cancelled) {
          clearPrivateState();
        }
      } catch (error) {
        console.error('Server state bootstrap failed', error);
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [authReady, authScopedKeys, applyRows, clearPrivateState, currentUser, fetchRows, isAdminUser, isAuthenticated, seedDefaultsIfMissing]);

  useEffect(() => {
    if (!isServerBackendEnabled || !bootstrapped) return;

    const originalSetItem = Storage.prototype.setItem;
    const originalRemoveItem = Storage.prototype.removeItem;

    const canWriteKey = (key: string) => {
      if (!shouldSyncStorageKey(key)) return false;
      if (isWritableByAdmin(key)) return isAuthenticated && isAdminUser;
      if (isStaffSharedWriteKey(key)) return isAuthenticated;
      if (isUserScopedKey(key)) {
        const ownerId = getStorageOwnerFromKey(key);
        return isAuthenticated && ownerId === currentUser?.id;
      }
      return false;
    };

    const scheduleFlush = () => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }
      flushTimerRef.current = window.setTimeout(async () => {
        const batch = Array.from(syncQueueRef.current.entries());
        syncQueueRef.current.clear();
        if (!batch.length) return;

        try {
          const upserts = batch
            .filter(([, value]) => value !== null)
            .map(([storageKey, value]) => ({
              storageKey,
              payloadText: value as string,
            }));
          const removals = batch.filter(([, value]) => value === null).map(([storageKey]) => storageKey);

          if (upserts.length) {
            await upsertStateRowsRequest(upserts);
          }

          if (removals.length) {
            await deleteStateKeysRequest(removals);
          }
        } catch (error) {
          console.error('Server state sync failed', error);
        }
      }, 250);
    };

    Storage.prototype.setItem = function patchedSetItem(key: string, value: string) {
      originalSetItem.call(this, key, value);
      if (this !== window.localStorage || hydratingRef.current || !canWriteKey(key)) return;
      syncQueueRef.current.set(key, value);
      scheduleFlush();
    };

    Storage.prototype.removeItem = function patchedRemoveItem(key: string) {
      originalRemoveItem.call(this, key);
      if (this !== window.localStorage || hydratingRef.current || !canWriteKey(key)) return;
      syncQueueRef.current.set(key, null);
      scheduleFlush();
    };

    return () => {
      Storage.prototype.setItem = originalSetItem;
      Storage.prototype.removeItem = originalRemoveItem;
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }
    };
  }, [bootstrapped, currentUser?.id, isAdminUser, isAuthenticated]);

  useEffect(() => {
    if (!isServerBackendEnabled || !bootstrapped) return;
    if (publicSanitizeDoneRef.current) return;

    publicSanitizeDoneRef.current = true;
    sanitizeLegacyHeroBannersInStorage();
    sanitizeLegacyInsightsInStorage();
    sanitizeLegacyJobPositionsInStorage();
  }, [bootstrapped]);

  useEffect(() => {
    if (!isServerBackendEnabled || !bootstrapped || !isAuthenticated || !isAdminUser) return;
    if (demoCleanupDoneRef.current) return;

    demoCleanupDoneRef.current = true;
    sanitizeLegacyDemoBusinessDataInStorage();
    sanitizeLegacyCrmDataInStorage();
    sanitizeLegacyTestimonialsInStorage();
    sanitizeLegacyHeroBannersInStorage();
    sanitizeLegacyInsightsInStorage();
    sanitizeLegacyJobPositionsInStorage();
  }, [bootstrapped, isAdminUser, isAuthenticated]);

  if (!bootstrapped) {
    return <LoadingShell />;
  }

  return <>{children}</>;
}

const RECOVERABLE_CONTENT_KEYS = new Set<string>([
  STORAGE_KEYS.insights,
  STORAGE_KEYS.jobPositions,
  STORAGE_KEYS.testimonials,
  STORAGE_KEYS.referenceClients,
  STORAGE_KEYS.adminProjects,
  STORAGE_KEYS.projectCosts,
  STORAGE_KEYS.timesheets,
  STORAGE_KEYS.contracts,
  STORAGE_KEYS.crmOpportunities,
  STORAGE_KEYS.crmContacts,
  STORAGE_KEYS.crmActivities,
]);

function payloadLooksRecoverable(storageKey: string, payloadText: string | null) {
  if (!payloadText || !RECOVERABLE_CONTENT_KEYS.has(storageKey)) {
    return false;
  }

  const trimmed = payloadText.trim();
  if (trimmed === '' || trimmed === '[]' || trimmed === '{}' || trimmed === 'null') {
    return false;
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;

    if (Array.isArray(parsed)) {
      return parsed.length > 0;
    }

    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed as Record<string, unknown>).length > 0;
    }

    return false;
  } catch {
    return false;
  }
}

function payloadLooksEmptyOrDefault(storageKey: string, payloadText: string | null) {
  if (!RECOVERABLE_CONTENT_KEYS.has(storageKey)) {
    return false;
  }

  if (!payloadText) {
    return true;
  }

  const trimmed = payloadText.trim();
  return trimmed === '' || trimmed === '[]' || trimmed === '{}' || trimmed === 'null';
}

function shouldRecoverServerRow(storageKey: string, localPayload: string | null, remotePayload: string | null) {
  if (!payloadLooksRecoverable(storageKey, localPayload)) {
    return false;
  }

  return payloadLooksEmptyOrDefault(storageKey, remotePayload);
}
